// W26 (2026-10-20): list reads for the Current Action real-contact
// cards.
//
//   useContactTimeline(customerId) -> GET /v1/contacts/:id/timeline
//   useContactHorses(customerId)   -> GET /v1/contacts/:id/horses
//
// Both share useContactReadGate + buildSessionHeaders with
// useContactProfile so there is exactly one definition of "real
// session" and one header contract. Status 'skipped' means preview /
// dev-mock mode; the caller keeps the design fixture in that case and
// ONLY in that case.
//
// A failed or empty read never falls back to fixture data: the card
// renders an honest empty / error state instead.

import { useEffect, useRef, useState } from 'react';

import { buildSessionHeaders, useContactReadGate } from './useContactProfile.js';

export interface TimelineItem {
  type: 'note' | 'call';
  id: string | null;
  at: string | null;
  body: string;
  title: string | null;
  ownerId: string | null;
  durationMs?: number | null;
  disposition?: string | null;
}

export interface HorseRecord {
  id: string | null;
  source?: 'contact_property' | 'custom_object';
  slot?: number;
  name: string | null;
  breed: string | null;
  age: string | null;
  gender?: string | null;
  discipline: string | null;
  products?: string | null;
  productsFeeding?: string | null;
  goal?: string | null;
  reason?: string | null;
}

export type CollectionStatus = 'loading' | 'ready' | 'error' | 'skipped';

export interface CollectionResult<T> {
  status: CollectionStatus;
  items: T[];
  errorCode?: string;
}

function useContactCollection<T>(
  customerId: string | null | undefined,
  subpath: string,
  key: string,
): CollectionResult<T> {
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;
  const [state, setState] = useState<CollectionResult<T>>(() => ({
    status: canFetch ? 'loading' : 'skipped',
    items: [],
  }));
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!canFetch || !customerId || !gate.session) {
      setState({ status: 'skipped', items: [] });
      return;
    }
    const requestId = `${customerId}:${subpath}:${Date.now()}`;
    latest.current = requestId;
    setState({ status: 'loading', items: [] });
    const headers = buildSessionHeaders(gate.session);
    const url = `/v1/contacts/${encodeURIComponent(customerId)}/${subpath}`;
    fetch(url, { method: 'GET', headers })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (!response.ok) {
          let errorCode = `HTTP_${response.status}`;
          try {
            const parsed = JSON.parse(await response.text()) as { errorCode?: string };
            if (parsed && typeof parsed.errorCode === 'string') errorCode = parsed.errorCode;
          } catch {
            /* non-JSON or empty error body */
          }
          setState({ status: 'error', items: [], errorCode });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          setState({ status: 'error', items: [], errorCode: 'BAD_JSON' });
          return;
        }
        const list = body && typeof body === 'object'
          ? (body as Record<string, unknown>)[key]
          : null;
        setState({ status: 'ready', items: Array.isArray(list) ? (list as T[]) : [] });
      })
      .catch(() => {
        if (latest.current !== requestId) return;
        setState({ status: 'error', items: [], errorCode: 'FETCH_FAILED' });
      });
    return () => {
      if (latest.current === requestId) latest.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, subpath, key, sessionIdentity, sessionToken]);

  return state;
}

export function useContactTimeline(
  customerId: string | null | undefined,
): CollectionResult<TimelineItem> {
  return useContactCollection<TimelineItem>(customerId, 'timeline', 'items');
}

export function useContactHorses(
  customerId: string | null | undefined,
): CollectionResult<HorseRecord> {
  return useContactCollection<HorseRecord>(customerId, 'horses', 'horses');
}
